// yield.ts — the Yield Engine.
//
// Every formula here consumes a quantity plotField.ts already computes.
// The ground prices everything: grading cost, array output, extraction
// rate, cable reach. Constants are named so they can be tuned.

import type { SubPlot, PlotClimate } from './plotField';
import {
  arrayAreaFor, dustOpticalDepth,
  solarOutputFraction, dayFraction as plotDayFraction,
} from './plotField.ts';
import type { Placement } from './structures';
import { PANEL_M2, kWPerPanelFrom, type PanelSite } from '../../shared/power.ts';

// ── Grading (§5.1) ──────────────────────────────────────────────────────────

export function gradeFactor(cell: SubPlot): number {
  const slopeTerm = (1 + cell.meanSlopeDeg / 4) ** 2;
  const roughTerm = 1 + cell.roughness_m / (0.008 * cell.radius_m);
  return slopeTerm * roughTerm;
}

export interface BuildCost {
  gradeFactor: number;
  /** Build cost multiplied by the grade factor. */
  cost: number;
  /** Build duration in sols, multiplied by grade + clearance. */
  sols: number;
  /** Why the build is blocked, if it is. */
  blocked?: string;
}

const BASE_COST: Record<string, number> = {
  solar: 50, extractor: 80, refinery: 120, workshop: 100,
  smelter: 140, fabricator: 110, assembler: 160, reclaimer: 110,
};
const BASE_SOLS: Record<string, number> = {
  solar: 1, extractor: 1.5, refinery: 2.5, workshop: 2,
  smelter: 3, fabricator: 2.5, assembler: 3.5, reclaimer: 2.5,
};

export function buildCost(
  buildingClass: string,
  cell: SubPlot,
  rockK?: number,
  rockSource?: string,
): BuildCost {
  if (cell.verdict === 'unbuildable') {
    const reason = cell.meanSlopeDeg >= 14
      ? `slope ${cell.meanSlopeDeg.toFixed(1)}° · limit 14°`
      : `roughness ${cell.roughness_m.toFixed(2)} m · limit ${(0.045 * cell.radius_m).toFixed(2)} m`;
    return { gradeFactor: Infinity, cost: Infinity, sols: Infinity, blocked: reason };
  }
  const gf = gradeFactor(cell);
  const base = BASE_COST[buildingClass] ?? 100;
  const baseSols = BASE_SOLS[buildingClass] ?? 2;
  const clearance = rockK != null && rockSource === 'measured'
    ? 1 + 4 * rockK
    : 1.5; // site median fallback
  return { gradeFactor: gf, cost: base * gf, sols: baseSols * gf * clearance };
}

// ── Power (§5.2) ─────────────────────────────────────────────────────────────

export { PANEL_M2 };

/**
 * The three numbers a panel's output is computed from, gathered off a cell
 * and a season. The server stores exactly these on a placement, so the grid
 * and the panel run the same expression rather than two copies of it.
 */
export function panelSiteAt(
  insolationWhSol: number, lsDeg: number, climate: PlotClimate,
): PanelSite {
  return {
    insolationWhSol,
    solLengthS: climate.solLengthS,
    // No dust curve is no dust term, not a clear-season baseline.
    dustTau: dustOpticalDepth(lsDeg, climate.dust) ?? 0,
  };
}

/**
 * A panel's output from an insolation, whatever measured or modelled that
 * insolation. The ranking in `bestHex.ts` reaches the formula through here,
 * where it has a number for ground it has never descended into and no SubPlot.
 */
export function kWPerPanelAt(
  insolationWhSol: number, lsDeg: number, climate: PlotClimate,
): number {
  return kWPerPanelFrom(panelSiteAt(insolationWhSol, lsDeg, climate));
}

export function kWPerPanel(cell: SubPlot, lsDeg: number, climate: PlotClimate): number {
  return kWPerPanelAt(cell.insolationWhSol, lsDeg, climate);
}

/**
 * Instantaneous kW per panel at a specific moment in the sol.
 *
 * Returns 0 at night. During the day, returns kWPerPanel / dayFraction so
 * the sol-average is preserved: a panel that makes X kW on average still
 * makes X kW when you integrate over the whole sol. The decision panel
 * continues to show the sol-average (`kWPerPanel`); the energy budget uses
 * this for real-time power availability.
 */
export function kWPerPanelNow(
  cell: SubPlot, lsDeg: number, solPhase: number, latDeg: number, climate: PlotClimate, decDeg?: number,
): number {
  const sun = solarOutputFraction(solPhase, latDeg, lsDeg, climate, decDeg);
  if (sun <= 0) return 0;
  const avg = kWPerPanel(cell, lsDeg, climate);
  // Avoid division by near-zero during polar night (no daylight at all).
  const df = plotDayFraction(latDeg, lsDeg, climate, decDeg);
  if (df < 0.01) return 0;
  // Scale so the sol-average is preserved: during the lit fraction, output
  // is avg/dayFraction; over the full sol that integrates back to avg.
  return avg / df;
}

export function arrayCapacity(areaM2: number): number {
  return Math.floor(areaM2 / PANEL_M2);
}

// ── Extractors (§5.3) ────────────────────────────────────────────────────────

export function workability(cell: SubPlot): number {
  const sTerm = Math.max(0, 1 - cell.meanSlopeDeg / 12);
  const rTerm = Math.max(0, 1 - cell.roughness_m / (0.030 * cell.radius_m));
  return 0.64 * sTerm + 0.36 * rTerm;
}

export function extractorRate(cell: SubPlot, baseRate = 1.0): number {
  return baseRate * (0.5 + 0.5 * workability(cell));
}

// ── Cable adjacency (§5.4) ───────────────────────────────────────────────────

export const CABLE_M = 600;
export const CABLE_KG_PER_100M = 10;

export function nearestPowerSource(
  x: number, z: number,
  placements: Placement[],
): { dist: number; source: Placement } | null {
  let best: { dist: number; source: Placement } | null = null;
  for (const p of placements) {
    // An array on a plot that has not loaded has no position to run a cable
    // to; it is on other ground than the cell being asked about.
    if (p.kind !== 'solar' || p.x === undefined || p.z === undefined) continue;
    const d = Math.sqrt((p.x - x) ** 2 + (p.z - z) ** 2);
    if (!best || d < best.dist) best = { dist: d, source: p };
  }
  return best;
}

export function cableBlocked(
  x: number, z: number,
  placements: Placement[],
): string | undefined {
  const src = nearestPowerSource(x, z, placements);
  if (!src) return 'no solar array placed';
  if (src.dist > CABLE_M) return `nearest array ${Math.round(src.dist)} m away · limit ${CABLE_M} m`;
  return undefined;
}

// ── Earned footprints (§8.3) ─────────────────────────────────────────────────

export function earnedHalfM(buildingClass: string, cell: SubPlot): number {
  if (buildingClass === 'solar') {
    if (!(cell.insolationWhSol > 0)) return 0;
    return Math.sqrt(arrayAreaFor(24e6, cell.insolationWhSol)) / 2;
  }
  const rockK = 0; // will come from plotField.meta.rocks when wired
  const baseM: Record<string, number> = {
    extractor: 30, refinery: 45, workshop: 35,
    smelter: 45, fabricator: 35, assembler: 50, reclaimer: 40,
  };
  const base = baseM[buildingClass] ?? 30;
  return base + 4 * (rockK * 10);
}

// ── Panel summary for the decision panel ─────────────────────────────────────

export interface CellYield {
  kWPerPanel: number;
  arrayCapacity: number;
  arraySolsToPlace: number;
  workability: number;
  extractorRate: number;
  gradeFactor: number;
  cableBlocked?: string;
}

export function cellYield(
  cell: SubPlot,
  lsDeg: number,
  climate: PlotClimate,
  placements: Placement[],
  rockK?: number,
  rockSource?: string,
): CellYield {
  const gf = gradeFactor(cell);
  const kw = kWPerPanel(cell, lsDeg, climate);
  const halfM = earnedHalfM('solar', cell);
  const areaM2 = (halfM * 2) ** 2;
  const cap = arrayCapacity(areaM2);
  const solarCost = buildCost('solar', cell, rockK, rockSource);

  return {
    kWPerPanel: kw,
    arrayCapacity: cap,
    arraySolsToPlace: solarCost.sols,
    workability: workability(cell),
    extractorRate: extractorRate(cell),
    gradeFactor: gf,
    cableBlocked: cableBlocked(cell.x, cell.z, placements),
  };
}
